import React, { useState, useRef, useEffect } from 'react';
import { getOsType, Os } from '@/utils/agent.ts';

interface BottomSheetProps {
  children: React.ReactNode;
  initialOffsetY: number;
}

const BottomSheet: React.FC<BottomSheetProps> = ({
  children,
  initialOffsetY,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [offsetPx, setOffsetPx] = useState(initialOffsetY);
  const [isDragging, setIsDragging] = useState(false);
  const startYRef = useRef(0);
  const currentOffsetRef = useRef(initialOffsetY);

  const isAndroid = getOsType() === Os.ANDROID;

  const onStart = (clientY: number) => {
    setIsDragging(true);
    startYRef.current = clientY;
    currentOffsetRef.current = offsetPx;
  };

  const onMove = (clientY: number) => {
    if (!isDragging) return;
    const deltaY = clientY - startYRef.current;
    const newOffset = currentOffsetRef.current + deltaY;
    if (newOffset >= 0 && newOffset <= window.innerHeight) {
      setOffsetPx(newOffset);
    }
  };

  const onEnd = (clientY: number) => {
    if (!isDragging) return;
    setIsDragging(false);
    const deltaY = clientY - startYRef.current;
    const threshold = 10;
    const newOffset =
      Math.abs(deltaY) < threshold ? offsetPx : deltaY > 0 ? initialOffsetY : 0;
    setOffsetPx(newOffset);
  };

  useEffect(() => {
    setOffsetPx(initialOffsetY);
  }, [initialOffsetY]);

  useEffect(() => {
    const handleResize = () => {
      setOffsetPx(initialOffsetY);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [initialOffsetY]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    onStart(e.clientY);
  };
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) =>
    onMove(e.clientY);
  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) =>
    onEnd(e.clientY);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) =>
    onStart(e.touches[0].clientY);
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) =>
    onMove(e.touches[0].clientY);
  const handleTouchEnd = (e: React.TouchEvent<HTMLDivElement>) =>
    onEnd(e.changedTouches[0].clientY);

  return (
    <div
      className="ixi-O-bottom-sheet"
      ref={sheetRef}
      style={{
        transform: `translateY(${offsetPx}px)`,
        transition: isDragging ? undefined : 'transform 0.3s ease',
      }}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div
        className="ixi-O-bottom-sheet__header handler"
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <span className="bar"></span>
      </div>
      <div
        className={`ixi-O-bottom-sheet__contents ${isAndroid ? 'android-scroll' : ''}`}
      >
        <div className="analyze-box">
          <div
            className="analyze-box__title-wrap handler2"
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
          >
            <p className="sub-title">지난 대화분석</p>
          </div>
          {children}
        </div>
      </div>
      <div className="ixi-O-bottom-sheet__footer"></div>
    </div>
  );
};

export default BottomSheet;
